/* Batch #14.152 — Onglet « Disponibilités » dans AppointmentsModule.
 *
 * Permet à l'admin de :
 *   - Configurer des plages récurrentes (ex: tous les lundis 9h-12h)
 *   - Ajouter des plages ponctuelles (override : "5 juin 14h-16h")
 *   - Bloquer des dates (blackout : "10 juin TOUTE LA JOURNÉE")
 *   - Voir un aperçu calendaire des prochains 14 jours
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import {
  Calendar, Plus, Trash2, Repeat, AlertCircle, CalendarX, Power, PowerOff, Clock, Loader2,
} from "lucide-react";

const DAY_LABELS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

export default function AvailabilityTab() {
  const [slots, setSlots] = useState([]);
  const [previewSlots, setPreviewSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [tab, setTab] = useState("recurring");
  const [form, setForm] = useState(defaultForm("recurring"));
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get("/admin/availability/slots"),
        api.get("/admin/availability/available", {
          params: {
            from: new Date().toISOString().slice(0, 10),
            to: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
          },
        }),
      ]);
      setSlots(s.data.items || []);
      setPreviewSlots(p.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const payload = {
      kind: tab,
      start_time: form.start_time,
      end_time: form.end_time,
      duration_minutes: parseInt(form.duration_minutes, 10) || 30,
      notes: form.notes || "",
    };
    if (tab === "recurring") {
      payload.day_of_week = parseInt(form.day_of_week, 10);
    } else {
      payload.date = form.date;
    }
    if (tab !== "blackout" && payload.start_time >= payload.end_time) {
      toast.error("L'heure de fin doit être après l'heure de début");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/availability/slots", payload);
      toast.success("Plage ajoutée");
      setCreating(false);
      setForm(defaultForm(tab));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const removeSlot = async (id) => {
    if (!window.confirm("Supprimer cette plage ?")) return;
    try {
      await api.delete(`/admin/availability/slots/${id}`);
      toast.success("Plage supprimée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const toggleSlot = async (id) => {
    try {
      // Batch #14.174 — POST au lieu de PATCH (proxy prod refuse PATCH preflight)
      await api.post(`/admin/availability/slots/${id}/toggle-post`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const recurringSlots = slots.filter((s) => s.kind === "recurring");
  const overrideSlots = slots.filter((s) => s.kind === "override");
  const blackoutSlots = slots.filter((s) => s.kind === "blackout");

  const groupedPreview = useMemo(() => {
    const map = {};
    for (const s of previewSlots) {
      map[s.date] = map[s.date] || [];
      map[s.date].push(s);
    }
    return map;
  }, [previewSlots]);

  return (
    <div className="space-y-6" data-testid="availability-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-indigo-600" /> Mes disponibilités
          </h2>
          <p className="text-xs text-gray-500">Définissez vos plages horaires — les clients ne verront que ces créneaux pour demander un RDV.</p>
        </div>
        <button
          onClick={() => { setCreating(!creating); setForm(defaultForm(tab)); }}
          className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 inline-flex items-center gap-1.5"
          data-testid="availability-add-btn"
        >
          <Plus className="w-4 h-4" /> Ajouter une plage
        </button>
      </div>

      {creating && (
        <div className="bg-indigo-50 border-2 border-indigo-200 rounded-xl p-4 space-y-3" data-testid="availability-form">
          <div className="flex items-center gap-2">
            {["recurring", "override", "blackout"].map((k) => (
              <button key={k} onClick={() => { setTab(k); setForm(defaultForm(k)); }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                        tab === k ? "bg-indigo-600 text-white" : "bg-white text-indigo-700 border border-indigo-200"
                      }`}
                      data-testid={`availability-tab-${k}`}>
                {k === "recurring" ? "🔁 Récurrent" : k === "override" ? "📌 Plage ponctuelle" : "🚫 Bloquer une date"}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {tab === "recurring" ? (
              <div>
                <label className="block text-[10px] uppercase font-semibold text-indigo-700 mb-1">Jour de la semaine</label>
                <select value={form.day_of_week} onChange={(e) => setForm({ ...form, day_of_week: e.target.value })}
                        className="w-full px-3 py-2 border border-indigo-200 rounded-lg text-sm bg-white"
                        data-testid="availability-dow">
                  {DAY_LABELS.map((l, i) => <option key={i} value={i}>{l}</option>)}
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-[10px] uppercase font-semibold text-indigo-700 mb-1">Date</label>
                <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                       min={new Date().toISOString().slice(0, 10)}
                       className="w-full px-3 py-2 border border-indigo-200 rounded-lg text-sm bg-white"
                       data-testid="availability-date" />
              </div>
            )}
            <div>
              <label className="block text-[10px] uppercase font-semibold text-indigo-700 mb-1">Heure début</label>
              <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                     className="w-full px-3 py-2 border border-indigo-200 rounded-lg text-sm bg-white"
                     data-testid="availability-start" />
            </div>
            <div>
              <label className="block text-[10px] uppercase font-semibold text-indigo-700 mb-1">Heure fin</label>
              <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                     className="w-full px-3 py-2 border border-indigo-200 rounded-lg text-sm bg-white"
                     data-testid="availability-end" />
            </div>
            {tab !== "blackout" && (
              <div>
                <label className="block text-[10px] uppercase font-semibold text-indigo-700 mb-1">Durée d'1 RDV (min)</label>
                <select value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
                        className="w-full px-3 py-2 border border-indigo-200 rounded-lg text-sm bg-white">
                  <option value={15}>15 min</option><option value={30}>30 min</option>
                  <option value={45}>45 min</option><option value={60}>1 h</option><option value={90}>1h30</option>
                </select>
              </div>
            )}
          </div>
          <input type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                 placeholder="Notes (optionnel) — ex: rappel personnel"
                 className="w-full px-3 py-2 border border-indigo-200 rounded-lg text-sm bg-white" />
          <div className="flex justify-end gap-2">
            <button onClick={() => setCreating(false)} className="px-3 py-2 rounded-lg text-sm text-indigo-700 hover:bg-indigo-100">
              Annuler
            </button>
            <button onClick={submit} disabled={busy}
                    className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-1"
                    data-testid="availability-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {busy ? "Ajout..." : "Ajouter"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-8 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : (
        <>
          {/* Récurrents */}
          <SlotSection title="🔁 Plages récurrentes (chaque semaine)" icon={Repeat}
                       items={recurringSlots} onRemove={removeSlot} onToggle={toggleSlot}
                       renderLabel={(s) => `${DAY_LABELS[s.day_of_week]} ${s.start_time} → ${s.end_time}`}
                       emptyText="Aucune plage récurrente. Ajoutez-en une pour permettre aux clients de réserver."
                       testid="availability-recurring-list" />
          {/* Overrides */}
          <SlotSection title="📌 Plages ponctuelles" icon={Calendar}
                       items={overrideSlots} onRemove={removeSlot} onToggle={toggleSlot}
                       renderLabel={(s) => `${formatDate(s.date)} ${s.start_time} → ${s.end_time}`}
                       emptyText="Aucune plage ponctuelle." testid="availability-override-list" />
          {/* Blackouts */}
          <SlotSection title="🚫 Dates bloquées (indisponible)" icon={CalendarX}
                       items={blackoutSlots} onRemove={removeSlot} onToggle={toggleSlot}
                       renderLabel={(s) => `${formatDate(s.date)} ${s.start_time} → ${s.end_time}`}
                       emptyText="Aucune date bloquée." testid="availability-blackout-list" />

          {/* Aperçu des 14 prochains jours */}
          <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="availability-preview">
            <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-emerald-600" /> Aperçu — 14 prochains jours dispo
            </h3>
            {Object.keys(groupedPreview).length === 0 ? (
              <p className="text-xs text-gray-400 italic">Aucun créneau libre dans les 14 prochains jours.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-72 overflow-y-auto">
                {Object.entries(groupedPreview).map(([date, items]) => (
                  <div key={date} className="bg-emerald-50 border border-emerald-200 rounded-lg p-2">
                    <div className="text-xs font-semibold text-emerald-900 mb-1">{formatDate(date)}</div>
                    {items.map((s, i) => (
                      <div key={i} className="text-[11px] text-emerald-700 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {s.start_time} → {s.end_time}
                        <span className="text-[9px] text-emerald-500">({s.source})</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SlotSection({ title, icon: Icon, items, onRemove, onToggle, renderLabel, emptyText, testid }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
        <Icon className="w-4 h-4" /> {title}
      </h3>
      {items.length === 0 ? (
        <p className="text-xs text-gray-400 italic flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> {emptyText}
        </p>
      ) : (
        <div className="space-y-1.5" data-testid={testid}>
          {items.map((s) => (
            <div key={s.id} className={`flex items-center justify-between px-3 py-2 rounded-lg border ${
              s.is_active ? "bg-slate-50 border-slate-200" : "bg-gray-100 border-gray-200 opacity-60"
            }`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-800">{renderLabel(s)}</div>
                {s.notes && <div className="text-[10px] text-gray-500 italic">{s.notes}</div>}
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => onToggle(s.id)}
                        className={`p-1.5 rounded ${s.is_active ? "text-emerald-600 hover:bg-emerald-50" : "text-gray-400 hover:bg-gray-100"}`}
                        title={s.is_active ? "Désactiver" : "Activer"}>
                  {s.is_active ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />}
                </button>
                <button onClick={() => onRemove(s.id)} className="p-1.5 rounded text-rose-600 hover:bg-rose-50"
                        title="Supprimer">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function defaultForm(kind) {
  const today = new Date().toISOString().slice(0, 10);
  return {
    day_of_week: 0,
    date: today,
    start_time: kind === "blackout" ? "00:00" : "09:00",
    end_time: kind === "blackout" ? "23:59" : "17:00",
    duration_minutes: 30,
    notes: "",
  };
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("fr-CA", { weekday: "short", day: "2-digit", month: "short" });
  } catch { return iso; }
}
